import React from 'react';
import round from 'lodash/round';
import { CusEcharts } from '@/components/AsyncComponent';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';
import { useClintMediaQuery } from '@/utils/hooks';

const Summary = () => {
  const {
    name,
    avg,
    latest_month_avg_salary: latestMonthAvgSalary,
    market_share: marketShare,
    adjacent_cities: adjacentCities,
  } = React.useContext(CityContext);
  const isMobile = useClintMediaQuery();
  const lineChartRef = React.useRef(null);
  const pieChartRef = React.useRef(null);
  const rank = adjacentCities.find((item) => item.current_city)?.rank;

  const lineConfig = React.useMemo<ECOption>(() => {
    const parseIntMonth = Object.keys(latestMonthAvgSalary)
      .slice(-3)
      .map((item) => parseInt(item.split('-')[1]));
    const avgSalary = Object.values(latestMonthAvgSalary)
      .slice(-3)
      .map((item) => round(item, -2));
    const seoInfo = parseIntMonth
      .map((item, index) => `${item}月的平均薪资为¥${avgSalary[index]}`)
      .join('，');

    return {
      grid: {
        show: false,
        tooltip: { show: true },
        top: 20,
        bottom: 20,
        left: 25,
        right: 25,
      },
      xAxis: {
        type: 'category',
        data: parseIntMonth,
        axisTick: { show: false },
        axisLine: { show: false },
        boundaryGap: false,
        axisLabel: {
          fontSize: 12,
          formatter: (value) => value + '月',
          color: 'rgba(0,0,0,0.6)',
        },
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      tooltip: {
        show: false,
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            type: 'solid',
            color: '#ea580c',
          },
        },
      },
      series: [
        {
          type: 'line',
          data: avgSalary,
          smooth: true,
          cursor: 'default',
          symbol: 'circle',
          symbolSize: 4,
          triggerLineEvent: false,
          lineStyle: {
            color: 'rgba(234,88,12,0.5)',
            width: 3,
          },
          itemStyle: { color: 'rgba(234,88,12)' },
          legendHoverLink: false,
          emphasis: {
            scale: 1.8,
            label: {
              show: true,
              fontSize: 12,
              color: 'rgba(0,0,0,0.6)',
              backgroundColor: 'transparent',
              formatter: ({ value }) => '¥' + value,
            },
          },
        },
      ],
      seoInfo,
    };
  }, [latestMonthAvgSalary]);

  const rangeData = React.useMemo(() => {
    const totalCount = marketShare
      .map(({ count }) => count)
      .reduce((a, b) => a + b, 0);

    return marketShare.map(({ name, id, count }) => {
      return { name, id, ratio: Math.round((count / totalCount) * 100) };
    });
  }, [marketShare]);

  const pieConfig = React.useMemo<ECOption>(() => {
    return {
      legend: {
        show: false,
      },
      grid: {
        show: false,
        tooltip: { show: false },
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      series: [
        {
          type: 'pie',
          silent: true,
          radius: isMobile ? [20, 110] : [20, 140],
          roseType: 'radius',
          center: ['50%', '50%'],
          itemStyle: {
            borderRadius: 5,
          },
          label: {
            show: true,
            backgroundColor: 'transparent',
            bleedMargin: 1,
          },
          labelLine: { length: 0 },
          emphasis: {
            disabled: true,
            label: {
              show: false,
            },
          },
          cursor: 'default',
          data: rangeData.map(({ name, ratio }) => {
            return { name, value: ratio };
          }),
        },
      ],
    };
  }, [rangeData, isMobile]);

  return (
    <div>
      <h1 className="text-5xl leading-14 font-semibold mb-6 tablet:mb-4">
        {`${name}招聘行业数据`}
      </h1>
      <p className="mb-9 text-base tablet:text-sm tabletl:leading-5.5">
        {`以下数据包括${name}近6个月内的招聘行业平均薪资、与${name}相近排名城市招聘相关情况对比、${name}不同招聘行业月薪对比、${name}不同招聘行业月薪变化趋势、${name}不同招聘行业的招聘市场需求对比、${name}招聘薪资前十的职位以及${name}招聘市场对于求职者工作年限的需求统计。后续请持续关注本网站的数据更新。本数据统计来源于各大平台发布的公开数据，仅供参考。`}
      </p>
      <div
        className="mb-8 px-5 py-4 rounded-lg bg-white/50 flex flex-col tablet:mb-12 tablet:px-10 tablet:py-5 tablet:flex-row"
        data-observer-id="summary-salary"
        data-ele-group="summary-salary"
        data-ele-id="content"
      >
        <div className="flex-grow">
          <p className="text-xl font-semibold mb-1">平均月薪</p>
          <h2 className="text-xs leading-5 text-black/60 mb-2">
            {`${name}招聘行业平均薪资`}
          </h2>
          <p className="text-3.25xl font-semibold mb-1">{`¥${round(
            avg,
            -2,
          )}`}</p>
          <p className="text-xs text-black/[0.45] mb-2">{`排名第${rank}`}</p>
          <div className="h-[110px] [&_canvas]:cursor-default tablet:max-w-[300px]">
            <CusEcharts chartRef={lineChartRef} config={lineConfig} />
          </div>
          <DataInterpretationSEO
            className="text-opacity-85 max-w-[400px]"
            dataEleGroup="summary-salary"
          >
            {`${name}招聘行业平均薪资为¥${round(
              avg,
              -2,
            )}，在所有城市中排名第${rank}，${name}招聘行业${
              lineConfig.seoInfo
            }。后续请持续关注本网站的数据更新。本数据统计来源于各大平台发布的公开数据，仅供参考。`}
          </DataInterpretationSEO>
        </div>
        <div className="w-full h-full tablet:w-[500px] tablet:flex-shrink-0 tablet:pl-10">
          <p className="font-semibold mb-1 mt-7 tablet:mt-0">市场占比</p>
          <p className="text-xs text-black/60 mb-1">{`${name}招聘行业市场占比`}</p>
          <div className="w-full h-[280px]">
            <CusEcharts chartRef={pieChartRef} config={pieConfig} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;
